import { useEffect, useState } from 'react';
import axios from 'axios';
import Cookies from 'universal-cookie';
import { Link as RouterLink } from 'react-router-dom';
import {
    Card,
    Container,
    Stack,
    Typography,
    Box,
    TextField,
    MenuItem,
    Checkbox,
    FormControlLabel,
    Table,
    TableHead,
    TableRow,
    TableBody,
    TableCell,
    TablePagination,
    Button,
    Chip,
    CircularProgress,
    Tabs,
    Tab,
} from '@mui/material';
import { API_URL } from '../../api';
import YearGroupSelect from '../../components/YearGroupSelect';
import { shortYear } from '../../constants/yearGroups';
import HowItWorksCard from '../../components/HowItWorksCard';

const cookies = new Cookies();
const authHeaders = () => ({ headers: { Authorization: cookies.get('TOKEN') } });

const TYPE_OPTIONS = [
    { value: '', label: 'Any type' },
    { value: 'tutoring', label: 'Tutoring enquiry' },
    { value: 'consultation', label: 'Consultation booking' },
];
const TYPE_LABEL = { tutoring: 'Tutoring', consultation: 'Consultation' };
const TYPE_COLOR = { tutoring: 'info', consultation: 'success' };

export default function ContactEnquiriesPage() {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [unread, setUnread] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [loading, setLoading] = useState(true);

    const [type, setType] = useState('');
    const [yearGroup, setYearGroup] = useState('');
    const [subject, setSubject] = useState('');
    const [subjects, setSubjects] = useState([]);
    const [q, setQ] = useState('');
    const [unreadOnly, setUnreadOnly] = useState(false);
    const [masterclassOnly, setMasterclassOnly] = useState(false);
    // Archived rows are hidden by default — admin has to tick "Include
    // archived" to see them. Matches the waitlist page pattern.
    // Tab-driven view: 'active' hides archived (default), 'archived' shows
    // only archived enquiries. Replaces the earlier "Include archived"
    // checkbox to match the pattern on Waiting list / Tutoring classes.
    const [tab, setTab] = useState('active');

    const buildQuery = () => {
        const params = new URLSearchParams();
        params.set('page', page + 1);
        params.set('limit', rowsPerPage);
        if (type) params.set('type', type);
        if (yearGroup) params.set('yearGroup', yearGroup);
        if (subject) params.set('subject', subject);
        if (q.trim()) params.set('q', q.trim());
        if (unreadOnly) params.set('read', 'false');
        if (masterclassOnly) params.set('masterclassOptIn', 'true');
        if (tab === 'archived') params.set('status', 'archived');
        else params.set('includeArchived', 'false');
        return params;
    };

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(
                `${API_URL}/api/admin/contact-enquiries?${buildQuery().toString()}`,
                authHeaders()
            );
            setItems(data.items || []);
            setTotal(data.total || 0);
            setUnread(data.unreadCount || 0);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (tab === 'replies') return; // SentRepliesTab manages its own fetch
        load();
        // eslint-disable-next-line
    }, [page, rowsPerPage, tab]);

    useEffect(() => {
        axios.get(`${API_URL}/api/subjects?activeOnly=true`)
            .then((r) => setSubjects(Array.isArray(r.data) ? r.data.map((s) => s.name).filter(Boolean) : []))
            .catch(() => {});
    }, []);

    const submit = (e) => {
        e.preventDefault();
        if (page !== 0) setPage(0);
        else load();
    };

    const downloadCsv = () => {
        const params = buildQuery();
        params.delete('page');
        params.delete('limit');
        const url = `${API_URL}/api/admin/contact-enquiries/export.csv?${params.toString()}`;
        // The endpoint requires the Authorization header. Easiest cross-browser way is to fetch as blob.
        fetch(url, { headers: { Authorization: cookies.get('TOKEN') } })
            .then((res) => {
                if (!res.ok) throw new Error('CSV export failed');
                return res.blob();
            })
            .then((blob) => {
                const objUrl = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = objUrl;
                a.download = `contact-enquiries-${new Date().toISOString().slice(0, 10)}.csv`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(objUrl);
            })
            .catch((err) => alert(err.message));
    };

    return (
        <Container>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h4">
                    Contact Enquiries{unread > 0 && <Chip label={`${unread} unread`} color="error" size="small" sx={{ ml: 2 }} />}
                </Typography>
                <Button
                    onClick={downloadCsv}
                    variant="contained"
                    sx={{ padding: '8px 22px', fontFamily: '"Inter"', fontWeight: 500, color: '#fff !important' }}
                >
                    Export CSV
                </Button>
            </Stack>
            <Box sx={{ mb: 3 }}>
                <HowItWorksCard>
                    <Typography variant="caption" component="div" color="text.secondary">
                        <strong>1.</strong> This is the queue of messages submitted through the public <strong>Contact Us</strong> form on the website.<br />
                        <strong>2.</strong> Filter by enquiry type (Tutoring / Consultation) and read/unread. The sidebar badge shows how many are still unread.<br />
                        <strong>3.</strong> Click a row to open the enquiry detail — from there you can <strong>Reply</strong>, <strong>Add a note</strong>, <strong>Send account invitation</strong>, <strong>Add to waiting list</strong>, <strong>Archive</strong>, <strong>Create invoice</strong>, or <strong>Mark read / unread</strong>.<br />
                        <strong>4.</strong> Click <strong>Export CSV</strong> at the top to download the full list.<br />
                    </Typography>
                </HowItWorksCard>
            </Box>

            {tab !== 'replies' && (
            <Card sx={{ p: 2, mb: 3 }}>
                <Box component="form" onSubmit={submit} sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    <TextField
                        label="Search name / email / child / school"
                        size="small"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        sx={{ minWidth: 280 }}
                    />
                    <TextField select size="small" label="Type" value={type} onChange={(e) => setType(e.target.value)} sx={{ minWidth: 180 }}>
                        {TYPE_OPTIONS.map((o) => (
                            <MenuItem key={o.value || 'any'} value={o.value}>{o.label}</MenuItem>
                        ))}
                    </TextField>
                    <YearGroupSelect
                        value={yearGroup}
                        onChange={setYearGroup}
                        sx={{ minWidth: 160 }}
                    />
                    <TextField select size="small" label="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} sx={{ minWidth: 180 }}>
                        <MenuItem value="">Any</MenuItem>
                        {subjects.map((s) => (
                            <MenuItem key={s} value={s}>{s}</MenuItem>
                        ))}
                    </TextField>
                    <Button type="submit" variant="contained">Filter</Button>
                    <FormControlLabel control={<Checkbox checked={masterclassOnly} onChange={(e) => setMasterclassOnly(e.target.checked)} />} label="Subscribed to updates" />
                    <FormControlLabel control={<Checkbox checked={unreadOnly} onChange={(e) => setUnreadOnly(e.target.checked)} />} label="Unread only" />
                </Box>
            </Card>
            )}

            <Tabs
                value={tab}
                onChange={(_, v) => { setTab(v); setPage(0); }}
                sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
            >
                <Tab value="active" label="Active" />
                <Tab value="archived" label="Archived" />
                <Tab value="replies" label="Sent replies" />
            </Tabs>

            {tab === 'replies' && <SentRepliesTab />}
            {tab !== 'replies' && (

            <Card>
                {loading ? (
                    <Stack alignItems="center" sx={{ py: 6 }}>
                        <CircularProgress />
                    </Stack>
                ) : (
                    <>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Received</TableCell>
                                    <TableCell>Type</TableCell>
                                    <TableCell>Parent</TableCell>
                                    <TableCell>Email</TableCell>
                                    <TableCell>Child</TableCell>
                                    <TableCell>Year group</TableCell>
                                    <TableCell>Subject</TableCell>
                                    <TableCell>Updates</TableCell>
                                    <TableCell align="right" sx={{ minWidth: 100 }} />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {items.map((it) => (
                                    <TableRow key={it._id} sx={{ background: it.read ? 'inherit' : 'rgba(0,153,255,0.05)' }}>
                                        <TableCell>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <Typography variant="body2">
                                                    {new Date(it.createdAt).toLocaleDateString('en-GB')}
                                                </Typography>
                                                {!it.read && <Chip size="small" label="New" color="error" />}
                                            </Stack>
                                        </TableCell>
                                        <TableCell>
                                            <Chip size="small" label={TYPE_LABEL[it.enquiryType] || it.enquiryType} color={TYPE_COLOR[it.enquiryType] || 'default'} />
                                        </TableCell>
                                        <TableCell>{it.firstName} {it.surname}</TableCell>
                                        <TableCell>{it.email}</TableCell>
                                        <TableCell>{it.childName || '—'}</TableCell>
                                        <TableCell>{shortYear(it.yearGroup) || it.level || '—'}</TableCell>
                                        <TableCell>{it.subject || '—'}</TableCell>
                                        <TableCell>
                                            {it.masterclassOptIn ? (
                                                <Chip size="small" label="Subscribed" color="success" />
                                            ) : (
                                                <Typography variant="body2" color="text.secondary">—</Typography>
                                            )}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Button component={RouterLink} to={`/contact-enquiries/${it._id}`} size="small">
                                                Open
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {items.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={9}>
                                            <Typography align="center" color="text.secondary" sx={{ py: 3 }}>
                                                No enquiries match the current filters.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                        <TablePagination
                            component="div"
                            count={total}
                            page={page}
                            onPageChange={(_, p) => setPage(p)}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={(e) => {
                                setRowsPerPage(parseInt(e.target.value, 10));
                                setPage(0);
                            }}
                        />
                    </>
                )}
            </Card>
            )}
        </Container>
    );
}

/**
 * "Sent replies" tab — flat list of every reply Meera has sent from any
 * enquiry, most-recent first, with a search box + date range and an
 * expandable body preview. Powers the "did that go out?" question
 * without hunting through each enquiry detail page.
 */
function SentRepliesTab() {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState('');
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');
    const [expanded, setExpanded] = useState({});

    const load = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            params.set('page', page + 1);
            params.set('limit', rowsPerPage);
            if (q.trim()) params.set('q', q.trim());
            if (from) params.set('from', from);
            if (to) params.set('to', to);
            const { data } = await axios.get(
                `${API_URL}/api/admin/contact-enquiries/replies?${params.toString()}`,
                authHeaders()
            );
            setItems(data.items || []);
            setTotal(data.total || 0);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [page, rowsPerPage]);

    const submit = (e) => {
        e.preventDefault();
        if (page !== 0) setPage(0);
        else load();
    };

    return (
        <>
            <Card sx={{ p: 2, mb: 3 }}>
                <Box component="form" onSubmit={submit} sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    <TextField
                        label="Search parent / email / subject / body"
                        size="small"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        sx={{ minWidth: 320 }}
                    />
                    <TextField
                        label="From"
                        size="small"
                        type="date"
                        value={from}
                        onChange={(e) => setFrom(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        label="To"
                        size="small"
                        type="date"
                        value={to}
                        onChange={(e) => setTo(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                    <Button type="submit" variant="contained">Filter</Button>
                </Box>
            </Card>
            <Card>
                {loading ? (
                    <Stack alignItems="center" sx={{ py: 6 }}>
                        <CircularProgress />
                    </Stack>
                ) : items.length === 0 ? (
                    <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
                        No replies match those filters.
                    </Typography>
                ) : (
                    <>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Sent</TableCell>
                                    <TableCell>To</TableCell>
                                    <TableCell>Subject</TableCell>
                                    <TableCell>Enquiry</TableCell>
                                    <TableCell align="right" />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {items.map((r, idx) => {
                                    const key = `${r.enquiryId}-${r.sentAt}`;
                                    const open = !!expanded[key];
                                    return (
                                        <>
                                            <TableRow key={key} hover>
                                                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                                    {new Date(r.sentAt).toLocaleString('en-GB')}
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">{r.parentName || '—'}</Typography>
                                                    <Typography variant="caption" color="text.secondary">{r.parentEmail}</Typography>
                                                </TableCell>
                                                <TableCell>{r.subject}</TableCell>
                                                <TableCell>
                                                    <Chip size="small" label={TYPE_LABEL[r.enquiryType] || r.enquiryType} color={TYPE_COLOR[r.enquiryType] || 'default'} sx={{ mr: 1 }} />
                                                    {r.childName || ''}
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Button size="small" onClick={() => setExpanded((x) => ({ ...x, [key]: !x[key] }))}>
                                                        {open ? 'Hide body' : 'View body'}
                                                    </Button>
                                                    <Button size="small" component={RouterLink} to={`/contact-enquiries/${r.enquiryId}`}>
                                                        Open
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                            {open && (
                                                <TableRow key={`${key}-body`}>
                                                    <TableCell colSpan={5} sx={{ bgcolor: 'action.hover' }}>
                                                        <Box sx={{ p: 1 }} dangerouslySetInnerHTML={{ __html: r.htmlBody }} />
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </>
                                    );
                                })}
                            </TableBody>
                        </Table>
                        <TablePagination
                            component="div"
                            count={total}
                            page={page}
                            onPageChange={(_, p) => setPage(p)}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={(e) => {
                                setRowsPerPage(parseInt(e.target.value, 10));
                                setPage(0);
                            }}
                        />
                    </>
                )}
            </Card>
        </>
    );
}